import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { toast } from "sonner";
import { Award, Shield, Dumbbell, Star, MapPin, Clock, Users, CalendarDays, Save, Plus, CheckCircle, Loader2, Copy, DollarSign } from "lucide-react";

const ROLE_ICONS = { coach: Award, trainer: Dumbbell, official: Shield };
const ROLE_COLORS = { coach: "bg-blue-100 text-blue-700", trainer: "bg-red-100 text-red-700", official: "bg-purple-100 text-purple-700" };
const STATUS_COLORS = { pending: "bg-amber-100 text-amber-800", verified: "bg-green-100 text-green-800", suspended: "bg-red-100 text-red-800" };

export default function SPDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [sp, setSp] = useState(null);
  const [loading, setLoading] = useState(true);
  const [bookings, setBookings] = useState([]);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [pRes, bRes] = await Promise.all([
        api.get("/sp/profile"),
        api.get("/sp/bookings"),
      ]);
      setSp(pRes.data);
      setBookings(bRes.data.bookings || []);
      setForm({ bio: pRes.data.bio || "", years_experience: pRes.data.years_experience || 0, service_locations: (pRes.data.service_locations || []).join(", "), insurance_declared: pRes.data.insurance_declared || false });
    } catch (err) {
      if (err.response?.status === 404) setSp(null);
    }
    setLoading(false);
  };

  const saveProfile = async () => {
    try {
      await api.put("/sp/profile", { ...form, service_locations: form.service_locations.split(",").map(s => s.trim()).filter(Boolean) });
      toast.success("Profile updated!");
      setEditing(false);
      fetchData();
    } catch { toast.error("Update failed"); }
  };

  const savePricing = async () => {
    try {
      const body = {};
      ["per_hour","per_game","per_session","half_day","full_day","series_discount_pct","travel_surcharge","peak_multiplier"].forEach(k => {
        const el = document.getElementById(`pricing-${k}`);
        if (el && el.value) body[k] = parseFloat(el.value);
      });
      await api.put("/sp/pricing", body);
      toast.success("Pricing saved!");
      fetchData();
    } catch { toast.error("Failed"); }
  };

  const copyReferral = () => {
    if (sp?.referral_key) {
      navigator.clipboard.writeText(sp.referral_key);
      toast.success("Referral code copied!");
    }
  };

  if (loading) return <div className="flex justify-center items-center min-h-[400px]"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>;

  if (!sp) {
    return (
      <div className="max-w-3xl mx-auto p-6 text-center py-20">
        <Award className="w-12 h-12 text-primary mx-auto mb-4" />
        <h1 className="font-heading text-3xl mb-3">Become a Sports Professional</h1>
        <p className="text-muted-foreground mb-6">Register as a Coach, Trainer, or Official to offer your services on SetPlay.</p>
        <Button onClick={() => navigate("/sp/register")} size="lg" className="rounded-full px-8">Get Started</Button>
      </div>
    );
  }

  const upcoming = bookings.filter(b => b.status === "confirmed" || b.status === "pending").slice(0, 5);
  const pricing = sp.pricing || {};

  return (
    <div className="max-w-5xl mx-auto p-4 sm:p-6 space-y-6" data-testid="sp-dashboard">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl">{sp.name}</h1>
          <div className="flex items-center gap-2 mt-1 flex-wrap">
            <Badge className={STATUS_COLORS[sp.verification_status]}>{sp.verification_status}</Badge>
            {sp.roles_enabled?.map(r => { const I = ROLE_ICONS[r] || Award; return <Badge key={r} className={ROLE_COLORS[r]}><I className="w-3 h-3 mr-1" />{r}</Badge>; })}
            <Badge variant="outline"><Star className="w-3 h-3 mr-0.5" />{sp.rating || 0} ({sp.total_ratings} reviews)</Badge>
            <Badge variant="outline"><CalendarDays className="w-3 h-3 mr-0.5" />{sp.total_sessions} sessions</Badge>
          </div>
        </div>
        <Button variant="outline" size="sm" onClick={() => setEditing(!editing)}>{editing ? "Cancel" : "Edit Profile"}</Button>
      </div>

      {/* Referral + Quick Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Card className="cursor-pointer" onClick={copyReferral}>
          <CardContent className="p-3 text-center">
            <Copy className="w-4 h-4 text-primary mx-auto mb-1" />
            <p className="text-xs font-mono font-bold text-primary">{sp.referral_key}</p>
            <p className="text-[9px] text-muted-foreground">Tap to copy referral</p>
          </CardContent>
        </Card>
        <Card><CardContent className="p-3 text-center">
          <p className="text-lg font-heading font-bold">{sp.years_experience}</p>
          <p className="text-[9px] text-muted-foreground">Years Experience</p>
        </CardContent></Card>
        <Card><CardContent className="p-3 text-center">
          <p className="text-lg font-heading font-bold">{sp.total_sessions}</p>
          <p className="text-[9px] text-muted-foreground">Total Sessions</p>
        </CardContent></Card>
        <Card><CardContent className="p-3 text-center">
          <p className="text-lg font-heading font-bold">{sp.commission_rate}%</p>
          <p className="text-[9px] text-muted-foreground">Commission Rate</p>
        </CardContent></Card>
      </div>

      {/* Profile Edit */}
      {editing && (
        <Card>
          <CardHeader><CardTitle className="font-heading text-base">Edit Profile</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            <div><Label className="text-xs">Bio</Label><Textarea value={form.bio} onChange={e => setForm({...form, bio: e.target.value})} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Years Experience</Label><Input type="number" value={form.years_experience} onChange={e => setForm({...form, years_experience: parseInt(e.target.value)||0})} /></div>
              <div><Label className="text-xs">Service Locations (comma-sep)</Label><Input value={form.service_locations} onChange={e => setForm({...form, service_locations: e.target.value})} /></div>
            </div>
            <Button onClick={saveProfile} className="rounded-full"><Save className="w-4 h-4 mr-1" />Save</Button>
          </CardContent>
        </Card>
      )}

      {/* Pricing */}
      <Card>
        <CardHeader><CardTitle className="font-heading text-base flex items-center gap-2"><DollarSign className="w-4 h-4" />Pricing</CardTitle></CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-3">
            {[["per_hour","Per Hour"],["per_game","Per Game"],["per_session","Per Session"],["half_day","Half Day"],["full_day","Full Day"],["series_discount_pct","Series Discount %"],["travel_surcharge","Travel Surcharge"],["peak_multiplier","Peak Multiplier"]].map(([k,l]) => (
              <div key={k}><Label className="text-[10px]">{l}</Label><Input id={`pricing-${k}`} type="number" step="any" defaultValue={pricing[k] || ""} placeholder="0" className="h-8 text-sm" /></div>
            ))}
          </div>
          <Button size="sm" onClick={savePricing} className="rounded-full"><Save className="w-3 h-3 mr-1" />Save Pricing</Button>
        </CardContent>
      </Card>

      {/* Upcoming Bookings */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="font-heading text-base">Upcoming Bookings</CardTitle>
            <Button variant="ghost" size="sm" onClick={() => navigate("/revenue-center?view=coach")}>View All</Button>
          </div>
        </CardHeader>
        <CardContent>
          {upcoming.length === 0 ? (
            <p className="text-center text-muted-foreground py-4 text-sm">No upcoming bookings</p>
          ) : (
            <div className="space-y-2">
              {upcoming.map(b => (
                <div key={b.booking_id} className="flex items-center justify-between py-2 border-b last:border-0">
                  <div>
                    <p className="text-sm font-medium">{b.client_name}</p>
                    <p className="text-xs text-muted-foreground">{b.date} {b.start_time}-{b.end_time} · {b.role}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-bold">₱{b.sp_payout}</p>
                    <Badge variant={b.status === "confirmed" ? "default" : "secondary"} className="text-[9px]">{b.status}</Badge>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
